import React, { useState, useEffect } from 'react';
import {
	Box,
	Typography,
	LinearProgress,
	Stepper,
	Step,
	StepLabel,
	StepContent,
	IconButton,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Modal,
	Alert,
	Paper,
	Grid,
	Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { getProcesses } from '../../actions/process';
import { formatDateMX } from '../../helpers/hours';
import Label from '../utils/Label';
import { BackgroundBox } from './BackgroundBox';
import { handleError, money } from '../../helpers/utils';

// Initial state for order data
const initialState = {
	number_order: 12345,
	startDate: '2024-05-01',
	endDate: '2024-05-30',
	biases: [
		{
			id: 1,
			prenda: 'Camisa',
			progreso: [{ is_delayed: false }, { is_delayed: true }],
		},
	],
	subpartidas: [
		{ id: 101, bias: 1 },
		{ id: 102, bias: 1 },
	],
};

// Stages of the process
const processStages = [
	'Mesa de control',
	'Logística',
	'Almacén',
	'Patronaje',
	'Corte',
	'Bordado y estampado',
	'Maquila Externa',
	'Maquila Interna',
	'Acabado',
	'Empaque',
	'Embarque',
];

// Component to display order information
const OrderInfo = ({
	number_order,
	progress,
	client_type,
	company_name,
	created_at,
	delivery_date,
	delivery_forecast,
	importAmount,
	canceled,
}) => (
	<Paper sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 3, p: 2 }}>
		<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
			<Box>
				<Typography variant='h6'>No. Pedido</Typography>
				<Typography variant='h5' sx={{ fontWeight: 'bold' }}>
					{number_order}
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
					<LinearProgress
						variant='determinate'
						value={progress}
						sx={{ width: '100px' }}
					/>
					<Typography variant='body2' sx={{ ml: 1 }}>
						{progress}%
					</Typography>
				</Box>
				<Label color={canceled ? 'error' : 'success'}>
					{canceled ? 'Cancelado' : 'Activo'}
				</Label>
			</Box>
			<Grid container spacing={2} sx={{ alignItems: 'center' }}>
				<Grid item xs={4} sx={{ textAlign: 'center' }}>
					<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
						Tipo de Cliente
					</Typography>
					<Typography variant='body2'>{client_type}</Typography>
				</Grid>
				<Grid item xs={4} sx={{ textAlign: 'center' }}>
					<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
						Cliente
					</Typography>
					<Typography variant='body2'>{company_name}</Typography>
				</Grid>
				<Grid item xs={4} sx={{ textAlign: 'center' }}>
					<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
						Fecha de Orden
					</Typography>
					<Typography variant='body2'>
						{formatDateMX(created_at)}
					</Typography>
				</Grid>
				<Grid item xs={4} sx={{ textAlign: 'center' }}>
					<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
						Fecha de Entrega
					</Typography>
					<Typography variant='body2'>
						{formatDateMX(delivery_date)}
					</Typography>
				</Grid>
				<Grid item xs={4} sx={{ textAlign: 'center' }}>
					<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
						Fecha Estimada
					</Typography>
					<Typography variant='body2'>
						{formatDateMX(delivery_forecast)}
					</Typography>
				</Grid>
				<Grid item xs={4} sx={{ textAlign: 'center' }}>
					<Typography variant='body2' sx={{ fontWeight: 'bold' }}>
						Importe
					</Typography>
					<Typography variant='body2'>
						{money(importAmount)}
					</Typography>
				</Grid>
			</Grid>
		</Box>
	</Paper>
);

// Component to display list of biases
const BiasesList = ({
	biases,
	currentBiasId,
	handleSelectBias,
	handleSelectSubpartida,
	subBiases,
}) => (
	<Box sx={{ flex: 1 }}>
		<Typography
			variant='h6'
			sx={{
				mb: 2,
				textAlign: 'center',
				fontWeight: 'bold',
				fontSize: '1rem',
			}}
		>
			Partidas
		</Typography>
		{biases.map((partida) => {
			const progresoPartida = partida.progress || 0;
			return (
				<Accordion
					key={partida.id}
					expanded={currentBiasId === partida.id}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						onClick={() => handleSelectBias(partida.id)}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								width: '100%',
								justifyContent: 'space-between',
								fontSize: '0.875rem',
							}}
						>
							<Typography sx={{ fontSize: '0.875rem' }}>
								{partida.garment.name}
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Typography
									sx={{
										mr: 2,
										fontWeight: 'bold',
										fontSize: '0.875rem',
									}}
								>{`${Math.round(
									progresoPartida
								)}%`}</Typography>
								<LinearProgress
									variant='determinate'
									value={progresoPartida}
									sx={{ width: '100px', height: '6px' }}
								/>
							</Box>
						</Box>
					</AccordionSummary>
					<AccordionDetails>
						<Typography
							sx={{ fontSize: '0.875rem', marginBottom: '10px' }}
						>
							<b>
								Tallas ({subBiases?.length}) - Progreso:{' '}
								{subBiases[0]?.progress || '0'}
								{'%'}
							</b>
						</Typography>
						{currentBiasId === partida.id &&
							subBiases
								.filter(
									(subpartida) =>
										subpartida.bias === partida.id
								)
								.map((subpartida) => (
									<Tooltip
										key={subpartida.id}
										title='Clic para ver detalle'
									>
										<Box
											onClick={() =>
												handleSelectSubpartida(
													subpartida.id
												)
											}
											sx={{
												cursor: 'pointer',
												mb: 1,
												p: 1,
												borderRadius: '4px',
												'&:hover': {
													backgroundColor: '#f0f0f0',
												},
												'&:active': {
													backgroundColor: '#e0e0e0',
												},
											}}
										>
											<Typography
												sx={{ fontSize: '0.875rem' }}
											>
												<b>Talla:</b>{' '}
												{subpartida.size.name} |{' '}
												<b>Unidades:</b>{' '}
												{subpartida.units}
											</Typography>
											<LinearProgress
												variant='determinate'
												value={subpartida.progress}
												sx={{
													width: '100%',
													height: '6px',
													mt: 1,
												}}
											/>
										</Box>
									</Tooltip>
								))}
					</AccordionDetails>
				</Accordion>
			);
		})}
	</Box>
);

// Component to display the history of processes for a selected subpartida
const HistoryList = ({ selectedSubpartida, historyList }) => (
	<Box
		sx={{
			flex: 1,
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			flexDirection: 'column',
		}}
	>
		{!selectedSubpartida ? (
			<Alert severity='info' sx={{ textAlign: 'center' }}>
				Seleccione una subpartida para ver su progreso
			</Alert>
		) : (
			<>
				<Typography
					variant='h6'
					sx={{
						mb: 2,
						textAlign: 'center',
						fontWeight: 'bold',
						fontSize: '1rem',
					}}
				>
					Áreas
				</Typography>
				<Typography
					variant='subtitle1'
					sx={{
						textAlign: 'center',
						mb: 2,
						fontWeight: 'bold',
						fontSize: '0.875rem',
					}}
				>
					Progreso de Subpartida
				</Typography>
				<Stepper orientation='vertical'>
					{processStages.map((stage, index) => {
						const process = historyList.find(
							(p) => p.name === stage
						);
						return (
							<Step key={index} active={Boolean(process)}>
								<StepLabel>{stage}</StepLabel>
								{process && (
									<StepContent>
										<Typography
											sx={{ fontSize: '0.875rem' }}
										>
											<b>Entrada:</b>{' '}
											{formatDateMX(process.entry_date)}
										</Typography>
										<Typography
											sx={{ fontSize: '0.875rem' }}
										>
											<b>Salida:</b>{' '}
											{formatDateMX(process.final_date)}
										</Typography>
										<Typography
											sx={{ fontSize: '0.875rem' }}
										>
											<b>Días asignados:</b>{' '}
											{process?.area?.days}
										</Typography>
										<Typography
											sx={{ fontSize: '0.875rem' }}
										>
											<b>Estado:</b>{' '}
											{process.entry_date &&
											process.final_date
												? 'Completado'
												: 'En curso'}
										</Typography>
									</StepContent>
								)}
							</Step>
						);
					})}
				</Stepper>
			</>
		)}
	</Box>
);

// Main component to display the history timeline modal
export const HistoryTimelineModal = ({
	open,
	onClose,
	orderData = initialState,
	biases = [],
	subBiases = [],
	handleGetSubBiases,
}) => {
	const {
		number_order,
		company_name,
		client_type,
		created_at,
		delivery_date,
		delivery_forecast,
		progress = 0,
		amount,
		canceled,
	} = orderData || initialState;
	const [currentSubbias, setCurrentSubbias] = useState(null);
	const [historyList, setHistoryList] = useState([]);
	const [currentBiasId, setCurrentBiasId] = useState(null);

	// Fetch history of processes for the selected subpartida
	useEffect(() => {
		if (currentSubbias) {
			// console.log('currentSubbias', currentSubbias);
			setHistoryList([]);
			const fetchHistory = async () => {
				try {
					const { data: processes } = await getProcesses(
						currentSubbias,
						currentBiasId
					);
					const formattedProcesses = processes.results.map(
						(process) => ({
							...process,
							name: process.area.name,
							is_delayed: process.status === 'DLY',
						})
					);
					setHistoryList(formattedProcesses);
				} catch (error) {
					handleError(error, 'No se pudo obtener el historial');
				}
			};
			fetchHistory();
		}
	}, [currentSubbias]);

	// Handle selecting a subpartida
	const handleSelectSubpartida = (subpartidaId) => {
		setCurrentSubbias(subpartidaId);
	};

	// Handle selecting a bias
	const handleSelectBias = (biasId) => {
		if (currentBiasId === biasId) {
			setCurrentBiasId(null);
		} else {
			setCurrentBiasId(biasId);
			handleGetSubBiases(biasId);
		}
	};

	return (
		<Modal
			open={open}
			onClose={() => {
				onClose();
				setCurrentSubbias(null);
				setHistoryList([]);
				setCurrentBiasId(null);
			}}
			aria-labelledby='modal-title'
			aria-describedby='modal-description'
		>
			<BackgroundBox>
				<Box sx={{ position: 'absolute', top: 0, right: 0 }}>
					<IconButton
						aria-label='close'
						onClick={() => {
							onClose();
							setCurrentSubbias(null);
							setHistoryList([]);
							setCurrentBiasId(null);
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>
				<OrderInfo
					number_order={number_order}
					progress={progress}
					client_type={client_type}
					company_name={company_name}
					created_at={created_at}
					delivery_date={delivery_date}
					delivery_forecast={delivery_forecast}
					canceled={canceled}
					importAmount={amount}
				/>
				<Box sx={{ display: 'flex', gap: 4 }}>
					<BiasesList
						biases={biases}
						currentBiasId={currentBiasId}
						handleSelectBias={handleSelectBias}
						handleSelectSubpartida={handleSelectSubpartida}
						subBiases={subBiases}
					/>
					<HistoryList
						selectedSubpartida={currentSubbias}
						historyList={historyList}
					/>
				</Box>
			</BackgroundBox>
		</Modal>
	);
};
